import React, { useState } from "react";
import { motion } from "framer-motion";
import { Ticket, Search, Plane, Hotel, Car, Package, Calendar } from "lucide-react";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ICON = { flight: Plane, hotel: Hotel, car: Car, package: Package };

export default function ManageBooking() {
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setBooking(null);
    try {
      const { data } = await api.get("/bookings/lookup", { params: { reference, email } });
      setBooking(data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Lookup failed"); }
    finally { setLoading(false); }
  };

  const Icon = booking ? (ICON[booking.type] || Package) : Ticket;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <div className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto"><Ticket className="w-7 h-7 text-brand" /></div>
        <h1 className="font-heading font-black text-3xl text-slate-950 mt-5">Manage your booking</h1>
        <p className="text-slate-500 mt-2">Enter your reference and email to view or amend your trip — no account needed.</p>
      </div>

      <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="manage-booking-form">
        <div><Label>Booking reference</Label><Input data-testid="lookup-reference" required value={reference} onChange={(e) => setReference(e.target.value.toUpperCase())} placeholder="WTH123ABC" className="mt-1.5 font-mono uppercase" /></div>
        <div><Label>Email address</Label><Input data-testid="lookup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1.5" /></div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button disabled={loading} data-testid="lookup-submit" className="w-full rounded-full bg-slate-950 hover:bg-slate-800 h-12 font-heading font-bold gap-2"><Search className="w-4 h-4" />{loading ? "Searching…" : "Find my booking"}</Button>
      </form>

      {booking && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="lookup-result">
          <div className="bg-slate-950 text-white p-6 flex items-center justify-between">
            <div><div className="text-xs uppercase tracking-wide text-slate-400">Reference</div><div className="font-heading font-black text-2xl tracking-wider">{booking.reference}</div></div>
            <Icon className="w-9 h-9 text-brand" />
          </div>
          <div className="p-6">
            <h2 className="font-heading font-bold text-lg text-slate-950">{booking.title}</h2>
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div><div className="text-slate-400">Status</div><div className="font-semibold capitalize text-slate-900">{booking.status}</div></div>
              <div><div className="text-slate-400">Traveller</div><div className="font-semibold text-slate-900">{booking.passenger?.name}</div></div>
              {booking.travel_date && <div><div className="text-slate-400">Travel date</div><div className="font-semibold text-slate-900 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{booking.travel_date}</div></div>}
              <div><div className="text-slate-400">Total</div><div className="font-heading font-black text-slate-900">{money(booking.amount, booking.currency)}</div></div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
